/* nt-favorites.js — Favorites on every product surface (Justin 2026-10-22)
 * ------------------------------------------------------------------
 * Hearts on: product pages (next to h1.product__title / h1.bsv4__title), collection cards
 * (.product-item, .nthp-card), and both quick-buy modals. Tapping saves the product itself (no
 * colour / variant) and shows a "Saved to Favorites · View · Undo" toast.
 *
 * Storage: the customer's account (metafield custom.favorites) through the Shopify app proxy
 * /apps/favorites (Lambda ninja-favorites verifies Shopify's signature + logged_in_customer_id).
 * Liquid prints the saved list into window.__ntFav (layout/theme.liquid) so hearts paint filled
 * on first render. Until the proxy answers, favorites live in localStorage (ntFav:<cid>), and a
 * local list is pushed to the account once it does. Guests: tapping a heart sends them to sign
 * in, and the pending heart is saved right after (ntFavPending).
 * The Favorites page = templates/collection.favorites.json (sections/nt-favorites-page.liquid).
 *
 * Ninja Patches port (2026-10-22) of ninjatransfers.com main cfdf9bbb. The same "Ninja Favorites" app
 * is installed on this store; its proxy reaches the same Lambda, which picks the store from the signed
 * `shop` param. NP-only change below: the guest sign-in return (new customer accounts).
 */
(function () {
  'use strict';
  if (window.NTFav) return;

  var CFG = window.__ntFav || { cid: null, items: [] };
  var CID = CFG.cid ? String(CFG.cid) : '';
  /* C523 (Justin 2026-10-28, "when not signed in the favorites icon shouldnt show"): guests get no hearts anywhere. The class lands before any
     heart is mounted (all of them come from this file), so none flashes in; nt-favorites.css hides them. */
  if (!CID) document.documentElement.classList.add('ntfav-guest');
  var PROXY = '/apps/favorites';
  var LS_KEY = 'ntFav:' + (CID || 'guest');
  var PENDING_KEY = 'ntFavPending';
  var FAV_URL = '/pages/welcome?tab=my-favorites';
  var items = Array.isArray(CFG.items) ? CFG.items.slice() : [];
  var remote = null;          // null = unknown, true = proxy works, false = local only
  var listeners = [];
  var DESKTOP = window.matchMedia('(min-width: 750px)');   /* desktop product-page pill: inline note, no toast */

  function lsGet(k) { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch (e) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) {} }
  function lsDel(k) { try { localStorage.removeItem(k); } catch (e) {} }

  /* The ACCOUNT is the source of truth (Liquid printed it into __ntFav). localStorage is only a
     mirror, plus a one-time hand-over of hearts saved before the account was connected — never
     merged again, or a heart removed elsewhere would come back (Justin 2026-10-22). */
  var MIGRATED_KEY = 'ntFavMigrated:' + (CID || 'guest');
  var local = lsGet(LS_KEY);
  if (!CID && Array.isArray(local)) items = local;

  function has(pid) { pid = Number(pid); return items.some(function (x) { return x.p === pid; }); }
  function count() { return items.length; }
  function emit() {
    lsSet(LS_KEY, items);
    paintAll();
    listeners.forEach(function (fn) { try { fn(items); } catch (e) {} });
    try { document.dispatchEvent(new CustomEvent('ntfav:change', { detail: { items: items } })); } catch (e) {}
  }

  function call(op, item) {
    if (!CID) return Promise.reject(new Error('guest'));
    var opts = { method: op === 'get' ? 'GET' : 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' } };
    if (item) { opts.headers['Content-Type'] = 'application/json'; opts.body = JSON.stringify({ item: item }); }
    return fetch(PROXY + '?op=' + op, opts).then(function (r) {
      var ct = r.headers.get('content-type') || '';
      if (!r.ok || ct.indexOf('json') === -1) throw new Error('proxy ' + r.status);
      return r.json();
    });
  }

  function syncFromServer() {
    if (!CID) return Promise.resolve();
    return call('get').then(function (j) {
      remote = true;
      items = Array.isArray(j.items) ? j.items : [];
      emit();
    }, function () { remote = false; });
  }

  var ready = (CID ? call('get').then(function (j) {
    remote = true;
    var server = Array.isArray(j.items) ? j.items : [];
    items = server;
    if (lsGet(MIGRATED_KEY) || !Array.isArray(local)) { lsSet(MIGRATED_KEY, 1); return; }
    lsSet(MIGRATED_KEY, 1);
    var extra = local.filter(function (x) { return x && x.p && !server.some(function (s) { return s.p === x.p; }); });
    return extra.reduce(function (p, it) { return p.then(function () { return call('add', it).then(function (j2) { items = j2.items || items; }); }); }, Promise.resolve());
  }, function () { remote = false; if (Array.isArray(local) && local.length && !items.length) items = local; }) : Promise.resolve()).then(function () { emit(); flushPending(); });

  /* Back/forward restores a frozen page, and other tabs change the list: re-read the account */
  window.addEventListener('pageshow', function (e) { if (e.persisted) syncFromServer(); });
  document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'visible' && remote) syncFromServer(); });
  window.addEventListener('storage', function (e) {
    if (e.key !== LS_KEY) return;
    try { var v = JSON.parse(e.newValue || '[]'); if (Array.isArray(v)) { items = v; paintAll(); listeners.forEach(function (fn) { try { fn(items); } catch (x) {} }); } } catch (x) {}
  });

  function add(item, opts) {
    item = { p: Number(item.p), h: String(item.h || ''), v: item.v ? Number(item.v) : null, c: item.c || null, t: Date.now() };
    if (!item.p) return;
    if (!CID) { lsSet(PENDING_KEY, item); goSignIn(); return; }
    items = [item].concat(items.filter(function (x) { return x.p !== item.p; }));
    emit();
    if (!(opts && (opts.silent || (opts.inline && DESKTOP.matches)))) toast(item);
    if (remote !== false) call('add', item).then(function (j) { remote = true; items = j.items || items; emit(); }, function () { remote = false; });
  }
  function remove(pid) {
    pid = Number(pid);
    var it = items.filter(function (x) { return x.p === pid; })[0];
    items = items.filter(function (x) { return x.p !== pid; });
    emit();
    if (it && remote !== false) call('remove', it).then(function (j) { items = j.items || items; emit(); }, function () {});
    return it;
  }
  function toggle(item, opts) { if (has(item.p)) { remove(item.p); return false; } add(item, opts); return true; }

  function goSignIn() {
    var back = location.pathname + location.search;
    /* NP runs new customer accounts, which ignore checkout_url / return_url: the post-login handler in
       layout/theme.liquid brings the shopper back from np_post_login_return instead */
    lsSet('np_post_login_return_ts', Date.now());
    try { localStorage.setItem('np_post_login_return', back); } catch (e) {}
    location.href = '/account/login?checkout_url=' + encodeURIComponent(back);
  }
  function flushPending() {
    var p = lsGet(PENDING_KEY);
    if (!p || !CID) return;
    lsDel(PENDING_KEY);
    add(p);
  }

  /* ---------------- toast ---------------- */
  var toastEl = null, toastTimer = null;
  function toast(item) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'ntfav-toast';
      toastEl.setAttribute('role', 'status');
      document.body.appendChild(toastEl);
    }
    toastEl.innerHTML = '<span class="ntfav-toast__heart" aria-hidden="true">' + HEART_FILLED + '</span>' +
      '<span class="ntfav-toast__text">Saved to Favorites</span>' +
      '<a class="ntfav-toast__view" href="' + FAV_URL + '" data-portal-tab="My Favorites">View</a>' +
      '<button type="button" class="ntfav-toast__undo">Undo</button>';
    toastEl.querySelector('.ntfav-toast__undo').onclick = function () { remove(item.p); hideToast(); };
    toastEl.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 4500);
  }
  function hideToast() { if (toastEl) toastEl.classList.remove('is-on'); }

  /* ---------------- hearts ---------------- */
  var HEART = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M12 21s-7.5-4.6-9.5-9.3C1.1 8.4 3.2 5 6.6 5c2 0 3.6 1.1 4.4 2.6h2C13.8 6.1 15.4 5 17.4 5c3.4 0 5.5 3.4 4.1 6.7C19.5 16.4 12 21 12 21z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/></svg>';
  var HEART_FILLED = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M12 21s-7.5-4.6-9.5-9.3C1.1 8.4 3.2 5 6.6 5c2 0 3.6 1.1 4.4 2.6h2C13.8 6.1 15.4 5 17.4 5c3.4 0 5.5 3.4 4.1 6.7C19.5 16.4 12 21 12 21z" fill="#EF4444" stroke="#EF4444" stroke-width="1.8" stroke-linejoin="round"/></svg>';

  function makeBtn(kind, data) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'ntfav-btn ntfav-btn--' + kind;
    b.setAttribute('data-ntfav-pid', data.p);
    if (data.h) b.setAttribute('data-ntfav-handle', data.h);
    b.innerHTML = '<span class="ntfav-btn__icon"></span>' + (kind === 'pdp' ? '<span class="ntfav-btn__label">Favorite</span>' : '');
    paint(b);
    return b;
  }
  function paint(b) {
    var on = has(b.getAttribute('data-ntfav-pid'));
    if (b.getAttribute('data-ntfav-on') === (on ? '1' : '0')) return;   /* no DOM write = no observer loop */
    b.setAttribute('data-ntfav-on', on ? '1' : '0');
    b.classList.toggle('is-on', on);
    b.setAttribute('aria-pressed', on ? 'true' : 'false');
    b.setAttribute('aria-label', on ? 'Remove from Favorites' : 'Add to Favorites');
    b.querySelector('.ntfav-btn__icon').innerHTML = on ? HEART_FILLED : HEART;
    var lab = b.querySelector('.ntfav-btn__label');
    if (lab) lab.textContent = on ? 'Favorited' : 'Favorite';
  }
  function paintAll() {
    document.querySelectorAll('.ntfav-btn').forEach(paint);
    document.querySelectorAll('[data-ntfav-note]').forEach(function (n) {
      var on = has(n.getAttribute('data-ntfav-note'));
      if (n.classList.contains('is-on') !== on) n.classList.toggle('is-on', on);
    });
    document.querySelectorAll('[data-nt-fav-badge]').forEach(function (el) {
      var n = String(count()), txt = count() ? n : '';
      if (el.getAttribute('data-count') !== n) el.setAttribute('data-count', n);
      if (el.textContent !== txt) el.textContent = txt;
    });
  }

  function onClick(e) {
    var b = e.target.closest && e.target.closest('.ntfav-btn');
    if (!b) return;
    e.preventDefault();
    e.stopPropagation();
    var pid = b.getAttribute('data-ntfav-pid');
    /* the product itself — no colour / variant (Justin 2026-10-22) */
    toggle({ p: pid, h: b.getAttribute('data-ntfav-handle') }, { inline: b.classList.contains('ntfav-btn--pdp') });
    if (has(pid)) pump(b);
  }
  document.addEventListener('click', onClick, true);

  /* C524 (Justin 2026-10-28, "can we have the heart do a little heart pump animation when turned on?"): only the heart that was just tapped
     on beats once (nt-favorites.css .ntfav-pump), so hearts already saved never pulse on page load. The icon span survives paint(), which
     only swaps the svg inside it. */
  function pump(b) {
    var ic = b.querySelector('.ntfav-btn__icon');
    if (!ic) return;
    ic.classList.remove('ntfav-pump');
    void ic.offsetWidth;   /* restart on a quick re-tap */
    ic.classList.add('ntfav-pump');
  }
  document.addEventListener('animationend', function (e) {
    if (e.animationName === 'ntfavPump') e.target.classList.remove('ntfav-pump');
  });

  function handleFromHref(href) {
    var m = String(href || '').match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  }

  /* product page: heart to the right of the title (pill on desktop, circle on phones) */
  function mountPdp() {
    var meta = (window.ShopifyAnalytics && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product) || null;
    var pid = meta && meta.id;
    if (!pid) {
      var pp = document.querySelector('product-page[data-id]');
      if (pp) pid = pp.getAttribute('data-id');
    }
    if (!pid) {
      try { pid = JSON.parse((document.getElementById('bsv4-config') || {}).textContent || '{}').productId; } catch (e) {}
    }
    if (!pid || !/^\/products\//.test(location.pathname)) return;
    var h1 = document.querySelector('h1.product__title, h1.bsv4__title');
    if (!h1) return;
    if (h1.parentNode.classList.contains('ntfav-titlewrap')) { mountPdpImage(pid); return; }
    var wrap = document.createElement('div');
    wrap.className = 'ntfav-titlewrap';
    /* by-size v4: the title block's "Hide after upload" toggle hides the heart + note with it */
    if (h1.classList.contains('bsv4-hide-uploaded')) wrap.classList.add('bsv4-hide-uploaded');
    h1.parentNode.insertBefore(wrap, h1);
    wrap.appendChild(h1);
    var col = document.createElement('div');
    col.className = 'ntfav-pdpcol';
    col.appendChild(makeBtn('pdp', { p: pid, h: handleFromHref(location.pathname) }));
    var note = document.createElement('p');
    note.className = 'ntfav-note';
    note.setAttribute('data-ntfav-note', pid);
    note.innerHTML = 'This has been saved to your <a href="' + FAV_URL + '" data-portal-tab="My Favorites">favorites</a>';
    col.appendChild(note);
    wrap.appendChild(col);
    mountPdpImage(pid);
  }
  /* phones: the heart sits in the main product image's top-right corner instead (CSS swaps them) */
  function mountPdpImage(pid) {
    /* the gallery frame, not a slide (slides scroll away): v4 stage, else the main gallery's slider viewport */
    var host = document.querySelector('.bsv4-gallery__stage');
    if (!host) { var slide = document.querySelector('.product-gallery-item'); host = slide && (slide.closest('.css-slider-viewport') || slide.closest('css-slider') || slide.parentNode); }
    if (!host || host.querySelector('.ntfav-btn--img')) return;
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    host.appendChild(makeBtn('img', { p: pid, h: handleFromHref(location.pathname) }));
  }

  /* collection cards */
  function mountCards(root) {
    (root || document).querySelectorAll('.product-item[id^="product-item-"], .nthp-card[id^="product-item-"]').forEach(function (card) {
      if (card.querySelector('.ntfav-btn')) return;
      var pid = card.id.replace('product-item-', '');
      if (!/^\d+$/.test(pid)) return;
      var link = card.matches('a') ? card : card.querySelector('a[href*="/products/"]');
      var host = card.querySelector('.product-item__image, .nthp-card__media') || card;
      if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
      host.appendChild(makeBtn('card', { p: pid, h: handleFromHref(link && link.getAttribute('href')) }));
    });
  }

  /* quick-buy modals: collection-card dialog (bh-modal-<id>) + global search modal */
  function mountModals(root) {
    (root || document).querySelectorAll('dialog[id^="bh-modal-"]').forEach(function (dlg) {
      var title = dlg.querySelector('.grt-product-modal__title');
      if (!title || dlg.querySelector('.ntfav-btn')) return;
      var pid = dlg.id.replace('bh-modal-', '');
      var link = dlg.querySelector('a[href*="/products/"]');
      var wrap = document.createElement('div');
      wrap.className = 'ntfav-titlewrap ntfav-titlewrap--modal';
      title.parentNode.insertBefore(wrap, title);
      wrap.appendChild(title);
      wrap.appendChild(makeBtn('modal', { p: pid, h: handleFromHref(link && link.getAttribute('href')) }));
    });
    var gTitle = document.querySelector('[data-grt-modal-title]');
    if (gTitle && !gTitle.parentNode.querySelector('.ntfav-btn')) {
      var gb = makeBtn('modal', { p: 0, h: '' });
      gb.hidden = true;
      gTitle.insertAdjacentElement('afterend', gb);
    }
    /* NP: the blanks quick buy (assets/np-blanks-quickbuy.js) is ONE .bqb-modal reused for every product: the heart
       sits beside its title and follows whichever product is loaded (syncBqbModal) */
    var bT = document.querySelector('.bqb-modal .bqb-title');
    if (bT && !bT.parentNode.classList.contains('ntfav-titlewrap')) {
      var bw = document.createElement('div');
      bw.className = 'ntfav-titlewrap ntfav-titlewrap--modal ntfav-titlewrap--bqb';
      bT.parentNode.insertBefore(bw, bT);
      bw.appendChild(bT);
      var bb = makeBtn('modal', { p: 0, h: '' });
      bb.hidden = true;
      bw.appendChild(bb);
    }
  }
  /* the global modal fills its title per product: look the id up from the handle */
  var gSeen = '';
  function syncGlobalModal() {
    var linkEl = document.querySelector('[data-grt-modal-product-link]');
    var gb = document.querySelector('[data-grt-modal-title] ~ .ntfav-btn');
    if (!linkEl || !gb) return;
    var h = handleFromHref(linkEl.getAttribute('href'));
    if (!h || h === gSeen) return;
    gSeen = h;
    fetch('/products/' + encodeURIComponent(h) + '.js').then(function (r) { return r.json(); }).then(function (p) {
      gb.setAttribute('data-ntfav-pid', p.id);
      gb.setAttribute('data-ntfav-handle', h);
      gb.hidden = false;
      gb.removeAttribute('data-ntfav-on');
      paint(gb);
    }).catch(function () {});
  }

  /* the quick buy fills its title and "View full details" link after /products/<handle>.js loads; until then (and while
     closed) the heart hides, so it never shows the previous product's state */
  var bqbSeen = '';
  function syncBqbModal() {
    var m = document.querySelector('.bqb-modal');
    var bb = m && m.querySelector('.ntfav-titlewrap--bqb .ntfav-btn');
    if (!bb) return;
    var t = m.querySelector('.bqb-title'), full = m.querySelector('.bqb-full');
    var loading = m.hasAttribute('hidden') || !t || !full || t.textContent === 'Loading...' || t.textContent === 'Could not load this product.';
    var h = loading ? '' : handleFromHref(full.getAttribute('href'));
    if (!h) { if (!bb.hidden) bb.hidden = true; bqbSeen = ''; return; }
    if (h === bqbSeen) return;
    bqbSeen = h;
    fetch('/products/' + encodeURIComponent(h) + '.js').then(function (r) { return r.json(); }).then(function (p) {
      if (bqbSeen !== h || !p || !p.id) return;
      bb.setAttribute('data-ntfav-pid', p.id);
      bb.setAttribute('data-ntfav-handle', h);
      bb.removeAttribute('data-ntfav-on');
      paint(bb);
      bb.hidden = false;
    }).catch(function () {});
  }

  function scan() { mountPdp(); mountCards(); mountModals(); syncGlobalModal(); syncBqbModal(); paintAll(); }
  var scanQueued = false;
  function queueScan() { if (scanQueued) return; scanQueued = true; setTimeout(function () { scanQueued = false; scan(); }, 120); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan); else scan();
  try { new MutationObserver(queueScan).observe(document.body, { childList: true, subtree: true }); } catch (e) {}

  window.NTFav = {
    items: function () { return items.slice(); }, has: has, add: add, remove: remove, toggle: toggle, count: count,
    ready: ready, onChange: function (fn) { listeners.push(fn); }, url: FAV_URL, remote: function () { return remote; }
  };
})();
